/**
 * PlayerProfileCard.js - Player Profile Display Component
 * 
 * Displays player information including avatar, stats, level, rank, and wallet balance
 * with a retro gaming aesthetic and real-time updates.
 */

export class PlayerProfileCard {
    constructor(uiManager) {
        this.uiManager = uiManager;
        this.isVisible = true;
        this.playerData = null;
        this.updateInterval = null;
        
        // Animation states
        this.levelAnimation = null;
        this.balanceAnimation = null;
        
        console.log('[PlayerProfileCard] Component initialized');
    }

    /**
     * Initialize the player profile card
     */
    async initialize() {
        this.createProfileStructure();
        this.setupEventListeners();
        this.startRealtimeUpdates();
        console.log('[PlayerProfileCard] Profile card initialized');
    }

    /**
     * Create the profile card HTML structure
     */
    createProfileStructure() {
        const container = document.getElementById('player-profile-container');
        if (!container) {
            const newContainer = document.createElement('div');
            newContainer.id = 'player-profile-container';
            newContainer.className = 'player-profile-container';
            document.body.appendChild(newContainer);
        }

        const profileHTML = `
            <div class="player-profile-card ${this.isVisible ? 'visible' : 'hidden'}">
                <div class="profile-header">
                    <div class="profile-title">PLAYER PROFILE</div>
                    <div class="profile-status online">● ONLINE</div>
                </div>
                
                <div class="profile-content">
                    <!-- Avatar Section -->
                    <div class="avatar-section">
                        <div class="avatar-frame">
                            <div class="avatar-glow"></div>
                            <img class="avatar-image" src="/assets/sprites/avatars/default.png" alt="Player Avatar">
                            <div class="level-badge">
                                <span class="level-number">42</span>
                            </div>
                        </div>
                    </div>
                    
                    <!-- Player Info -->
                    <div class="player-info">
                        <div class="player-name" id="player-name">CYBERRACER2024</div>
                        <div class="player-rank">
                            <span class="rank-icon">◆◆◆◆◇</span>
                            <span class="rank-text">DIAMOND III</span>
                        </div>
                        
                        <!-- Level Progress -->
                        <div class="level-progress">
                            <div class="progress-label">
                                <span>LEVEL 42</span>
                                <span class="progress-percent">85%</span>
                            </div>
                            <div class="progress-bar">
                                <div class="progress-fill" style="width: 85%;">
                                    <div class="progress-glow"></div>
                                </div>
                            </div>
                            <div class="progress-exp">8,500 / 10,000 XP</div>
                        </div>
                    </div>
                    
                    <!-- Statistics Section -->
                    <div class="stats-section">
                        <div class="stats-header">STATISTICS</div>
                        <div class="stats-grid">
                            <div class="stat-item">
                                <span class="stat-label">RACES</span>
                                <span class="stat-value">1,247</span>
                            </div>
                            <div class="stat-item">
                                <span class="stat-label">WINS</span>
                                <span class="stat-value">421</span>
                            </div>
                            <div class="stat-item">
                                <span class="stat-label">WIN RATE</span>
                                <span class="stat-value highlight">33.7%</span>
                            </div>
                            <div class="stat-item">
                                <span class="stat-label">BEST TIME</span>
                                <span class="stat-value">1:23.45</span>
                            </div>
                            <div class="stat-item">
                                <span class="stat-label">PROFIT</span>
                                <span class="stat-value profit">+$12,450</span>
                            </div>
                            <div class="stat-item">
                                <span class="stat-label">STREAK</span>
                                <span class="stat-value">W-W-L-W</span>
                            </div>
                        </div>
                    </div>
                    
                    <!-- Wallet Section -->
                    <div class="wallet-section">
                        <div class="wallet-header">
                            <span class="wallet-icon">💰</span>
                            <span class="wallet-label">WALLET</span>
                        </div>
                        <div class="wallet-balance">
                            <span class="currency-symbol">$</span>
                            <span class="balance-amount" id="wallet-balance">12,450</span>
                        </div>
                        <div class="wallet-actions">
                            <button class="wallet-btn deposit">DEPOSIT</button>
                            <button class="wallet-btn withdraw">WITHDRAW</button>
                        </div>
                    </div>
                    
                    <!-- Achievements Section -->
                    <div class="achievements-section">
                        <div class="achievements-header">ACHIEVEMENTS</div>
                        <div class="achievements-list">
                            <div class="achievement-badge" title="Speed Demon">🏆</div>
                            <div class="achievement-badge" title="Lucky Streak x10">🎯</div>
                            <div class="achievement-badge" title="Betting Master">💎</div>
                            <div class="achievement-badge" title="Perfect Predictor">🔮</div>
                            <div class="achievement-badge locked" title="Locked">🔒</div>
                            <div class="achievement-badge locked" title="Locked">🔒</div>
                        </div>
                        <button class="view-all-btn">VIEW ALL...</button>
                    </div>
                </div>
            </div>
        `;

        const container2 = document.getElementById('player-profile-container');
        container2.innerHTML = profileHTML;
    }

    /**
     * Setup event listeners
     */
    setupEventListeners() {
        // Wallet actions
        const depositBtn = document.querySelector('.wallet-btn.deposit');
        const withdrawBtn = document.querySelector('.wallet-btn.withdraw');
        
        if (depositBtn) {
            depositBtn.addEventListener('click', () => this.handleDeposit());
        }
        
        if (withdrawBtn) {
            withdrawBtn.addEventListener('click', () => this.handleWithdraw());
        }
        
        // View all achievements
        const viewAllBtn = document.querySelector('.view-all-btn');
        if (viewAllBtn) {
            viewAllBtn.addEventListener('click', () => this.showAllAchievements());
        }
        
        // Avatar click for customization
        const avatarFrame = document.querySelector('.avatar-frame');
        if (avatarFrame) {
            avatarFrame.addEventListener('click', () => this.openAvatarCustomization());
        }
    }

    /**
     * Update player data
     */
    updatePlayerData(data) {
        this.playerData = data;
        
        // Update name
        const nameElement = document.getElementById('player-name');
        if (nameElement && data.name) {
            nameElement.textContent = data.name.toUpperCase();
        }
        
        // Update level and progress
        if (data.level) {
            this.updateLevel(data.level, data.experience);
        }
        
        // Update stats
        if (data.stats) {
            this.updateStatistics(data.stats);
        }
        
        // Update wallet
        if (data.balance !== undefined) {
            this.updateBalance(data.balance);
        }
        
        // Update rank
        if (data.rank) {
            this.updateRank(data.rank);
        }
    }

    /**
     * Update level with animation
     */
    updateLevel(level, experience) {
        const levelBadge = document.querySelector('.level-number');
        const progressBar = document.querySelector('.progress-fill');
        const progressPercent = document.querySelector('.progress-percent');
        const progressExp = document.querySelector('.progress-exp');
        
        if (levelBadge) {
            levelBadge.textContent = level;
        }
        
        const expForLevel = level * 10000; // Example calculation
        const expProgress = (experience % expForLevel) / expForLevel * 100;
        
        if (progressBar) {
            progressBar.style.width = `${expProgress}%`;
        }
        
        if (progressPercent) {
            progressPercent.textContent = `${Math.floor(expProgress)}%`;
        }
        
        if (progressExp) {
            progressExp.textContent = `${experience % expForLevel} / ${expForLevel} XP`;
        }
        
        // Animate level up
        if (this.playerData && this.playerData.level < level) {
            this.animateLevelUp();
        }
    }

    /**
     * Update statistics
     */
    updateStatistics(stats) {
        const statElements = {
            races: document.querySelector('.stat-item:nth-child(1) .stat-value'),
            wins: document.querySelector('.stat-item:nth-child(2) .stat-value'),
            winRate: document.querySelector('.stat-item:nth-child(3) .stat-value'),
            bestTime: document.querySelector('.stat-item:nth-child(4) .stat-value'),
            profit: document.querySelector('.stat-item:nth-child(5) .stat-value'),
            streak: document.querySelector('.stat-item:nth-child(6) .stat-value')
        };
        
        if (statElements.races && stats.totalRaces) {
            statElements.races.textContent = stats.totalRaces.toLocaleString();
        }
        
        if (statElements.wins && stats.wins) {
            statElements.wins.textContent = stats.wins.toLocaleString();
        }
        
        if (statElements.winRate && stats.winRate) {
            statElements.winRate.textContent = `${stats.winRate.toFixed(1)}%`;
        }
        
        if (statElements.bestTime && stats.bestTime) {
            statElements.bestTime.textContent = this.formatTime(stats.bestTime);
        }
        
        if (statElements.profit && stats.profit !== undefined) {
            const profitValue = stats.profit;
            statElements.profit.textContent = profitValue >= 0 ? `+$${profitValue.toLocaleString()}` : `-$${Math.abs(profitValue).toLocaleString()}`;
            statElements.profit.className = `stat-value ${profitValue >= 0 ? 'profit' : 'loss'}`;
        }
        
        if (statElements.streak && stats.recentResults) {
            statElements.streak.textContent = stats.recentResults.slice(0, 4).map(r => r ? 'W' : 'L').join('-');
        }
    }

    /**
     * Update balance with animation
     */
    updateBalance(newBalance) {
        const balanceElement = document.getElementById('wallet-balance');
        if (!balanceElement) return;
        
        const currentBalance = parseInt(balanceElement.textContent.replace(/,/g, ''));
        
        if (currentBalance !== newBalance) {
            // Animate balance change
            this.animateBalanceChange(currentBalance, newBalance);
        }
    }

    /**
     * Animate balance change
     */
    animateBalanceChange(from, to) {
        const balanceElement = document.getElementById('wallet-balance');
        if (!balanceElement) return;
        
        const duration = 1000;
        const startTime = Date.now();
        
        if (this.balanceAnimation) {
            cancelAnimationFrame(this.balanceAnimation);
        }
        
        const animate = () => {
            const now = Date.now();
            const progress = Math.min((now - startTime) / duration, 1);
            const easeProgress = this.easeOutCubic(progress);
            
            const currentValue = Math.floor(from + (to - from) * easeProgress);
            balanceElement.textContent = currentValue.toLocaleString();
            
            // Add flash effect
            if (to > from) {
                balanceElement.classList.add('balance-increase');
            } else if (to < from) {
                balanceElement.classList.add('balance-decrease');
            }
            
            if (progress < 1) {
                this.balanceAnimation = requestAnimationFrame(animate);
            } else {
                balanceElement.classList.remove('balance-increase', 'balance-decrease');
            }
        };
        
        animate();
    }

    /**
     * Update rank display
     */
    updateRank(rank) {
        const rankIcon = document.querySelector('.rank-icon');
        const rankText = document.querySelector('.rank-text');
        
        if (rankIcon && rankText) {
            const rankData = this.getRankData(rank);
            rankIcon.textContent = rankData.icon;
            rankText.textContent = rankData.name;
        }
    }

    /**
     * Get rank icon and name
     */
    getRankData(rank) {
        const ranks = {
            'bronze': { icon: '◆◇◇◇◇', name: 'BRONZE' },
            'silver': { icon: '◆◆◇◇◇', name: 'SILVER' },
            'gold': { icon: '◆◆◆◇◇', name: 'GOLD' },
            'platinum': { icon: '◆◆◆◆◇', name: 'PLATINUM' },
            'diamond': { icon: '◆◆◆◆◆', name: 'DIAMOND' },
            'master': { icon: '★★★★★', name: 'MASTER' }
        };
        
        return ranks[rank] || ranks['bronze'];
    }

    /**
     * Animate level up effect
     */
    animateLevelUp() {
        const levelBadge = document.querySelector('.level-badge');
        if (levelBadge) {
            levelBadge.classList.add('level-up-animation');
            setTimeout(() => {
                levelBadge.classList.remove('level-up-animation');
            }, 2000);
        }
        
        // Show notification
        if (this.uiManager) {
            this.uiManager.showNotification('LEVEL UP!', 'success');
        }
    }

    /**
     * Handle deposit action
     */
    handleDeposit() {
        console.log('[PlayerProfileCard] Deposit clicked');
        if (this.uiManager) {
            this.uiManager.emit('deposit-requested');
        }
    }

    /**
     * Handle withdraw action
     */
    handleWithdraw() {
        console.log('[PlayerProfileCard] Withdraw clicked');
        if (this.uiManager) {
            this.uiManager.emit('withdraw-requested');
        }
    }

    /**
     * Show all achievements
     */
    showAllAchievements() {
        console.log('[PlayerProfileCard] Show all achievements');
        if (this.uiManager) {
            this.uiManager.emit('show-achievements');
        }
    }

    /**
     * Open avatar customization
     */
    openAvatarCustomization() {
        console.log('[PlayerProfileCard] Open avatar customization');
        if (this.uiManager) {
            this.uiManager.emit('customize-avatar');
        }
    }

    /**
     * Start real-time updates
     */
    startRealtimeUpdates() {
        // Update every 5 seconds
        this.updateInterval = setInterval(() => {
            if (this.uiManager) {
                this.uiManager.emit('request-player-update');
            }
        }, 5000);
    }

    /**
     * Format time display
     */
    formatTime(seconds) {
        const minutes = Math.floor(seconds / 60);
        const secs = (seconds % 60).toFixed(2);
        return `${minutes}:${secs.padStart(5, '0')}`;
    }

    /**
     * Easing function for animations
     */
    easeOutCubic(t) {
        return 1 - Math.pow(1 - t, 3);
    }

    /**
     * Show/hide profile card
     */
    toggle() {
        this.isVisible = !this.isVisible;
        const card = document.querySelector('.player-profile-card');
        if (card) {
            card.classList.toggle('visible', this.isVisible);
            card.classList.toggle('hidden', !this.isVisible);
        }
    }

    /**
     * Cleanup
     */
    destroy() {
        if (this.updateInterval) {
            clearInterval(this.updateInterval);
        }
        
        if (this.balanceAnimation) {
            cancelAnimationFrame(this.balanceAnimation);
        }
        
        const container = document.getElementById('player-profile-container');
        if (container) {
            container.remove();
        }
    }
}

// Export as ES module
export default PlayerProfileCard;